import React, { useEffect, useState, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import API from '../../Services/api';
import { Tooltip } from "react-tooltip";

const DEFAULT_CENTER = [51.4831, -0.0066];
const CIRCLE_RADIUS = 5000;

function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const customIcon = L.divIcon({
  html: `<img src="/images/recycle_rotate.png" style="animation: spin 2s ease-in-out infinite; width: 45px; height: 45px;" />`,
  iconSize: [45, 45],
  iconAnchor: [22.5, 45],
  popupAnchor: [0, -45],
  className: "bg-transparent border-none",
});

function makeCompassIcon(label) {
  return L.divIcon({
    html: `<div style="background:rgba(0,77,63,0.8);color:white;font-weight:bold;font-size:12px;width:24px;height:24px;display:flex;align-items:center;justify-content:center;border-radius:4px;">${label}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    className: 'bg-transparent border-none',
  });
}

function CompassMarkers() {
  const degToRad = (d) => d * Math.PI / 180;
  const [lat, lon] = DEFAULT_CENTER;
  const latOffset = CIRCLE_RADIUS / 111111;
  const lonOffset = CIRCLE_RADIUS / (111111 * Math.cos(degToRad(lat)));
  const points = [
    { label: 'N', pos: [lat + latOffset, lon] },
    { label: 'S', pos: [lat - latOffset, lon] },
    { label: 'E', pos: [lat, lon + lonOffset] },
    { label: 'W', pos: [lat, lon - lonOffset] },
  ];
  return (
    <>
      {points.map(({ label, pos }) => (
        <Marker key={label} position={pos} icon={makeCompassIcon(label)} interactive={false} />
      ))}
    </>
  );
}

function CustomZoomControls() {
  const map = useMap();
  useEffect(() => {
    const zoomControls = L.control({ position: 'topright' });
    zoomControls.onAdd = () => {
      const div = L.DomUtil.create('div', 'custom-zoom-controls');
      div.innerHTML = `
        <div class="fixed right-6 flex flex-col bg-white p-2 border border-[#E2E8F0] rounded-lg">
          <button class="zoom-btn zoom-in text-3xl text-[#004d3f] flex items-center justify-center hover:bg-[#F1FAF7] p-1 rounded-full mb-2">
            <i class="fas fa-plus"></i>
          </button>
          <div class="border-t border-[#004d3f] my-2"></div>
          <button class="zoom-btn zoom-out text-3xl text-[#004d3f] flex items-center justify-center hover:bg-[#F1FAF7] p-1 rounded-full">
            <i class="fas fa-minus"></i>
          </button>
        </div>
      `;
      div.style.cursor = 'pointer';
      div.querySelector('.zoom-in').onclick = () => map.zoomIn();
      div.querySelector('.zoom-out').onclick = () => map.zoomOut();
      return div;
    };
    zoomControls.addTo(map);
    return () => zoomControls.remove();
  }, [map]);
  return null;
}

function CustomAttribution() {
  const map = useMap();
  useEffect(() => {
    map.attributionControl.setPrefix('<a href="/" class="text-[#29752B]" target="_blank">e-save</a>');
  }, [map]);
  return null;
}

function PanToLocation({ location }) {
  const map = useMap();
  useEffect(() => {
    if (location) map.flyTo(location, Math.max(map.getZoom(), 14));
  }, [location, map]);
  return null;
}

/* ─── Map Search Box ─────────────────────────────────────── */
function MapSearchBox({ collectionPoints, setSelectedLocation, onMarkerClick, initialQuery }) {
  const [query, setQuery] = useState(initialQuery || '');
  const [suggestions, setSuggestions] = useState({ centres: [], locations: [] });
  const [showDrop, setShowDrop] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDrop(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (!query.trim()) {
      setSuggestions({ centres: [], locations: [] });
      return;
    }
    const q = query.toLowerCase();
    const centres = collectionPoints
      .filter(p => p.name.toLowerCase().includes(q) || (p.address || '').toLowerCase().includes(q))
      .slice(0, 4);
    setSuggestions(prev => ({ ...prev, centres }));
  }, [query, collectionPoints]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim() || query.length < 2) {
      setSuggestions(prev => ({ ...prev, locations: [] }));
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=4&countrycodes=gb`,
          { headers: { 'Accept-Language': 'en' } }
        );
        const data = await res.json();
        setSuggestions(prev => ({
          ...prev,
          locations: data.map(r => ({
            name: r.display_name,
            short: r.name || r.display_name.split(',')[0],
            lat: parseFloat(r.lat),
            lon: parseFloat(r.lon),
            type: r.type,
          })),
        }));
      } catch {
        /* ignore network errors in suggestions */
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const handleSelect = (lat, lon, point = null) => {
    setSelectedLocation([lat, lon]);
    setShowDrop(false);
    setQuery('');
    setSuggestions({ centres: [], locations: [] });
    if (point && onMarkerClick) onMarkerClick(point);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && suggestions.centres[0]) {
      const p = suggestions.centres[0];
      const [lon, lat] = p.location.coordinates;
      handleSelect(lat, lon, p);
    } else if (e.key === 'Enter' && suggestions.locations[0]) {
      const l = suggestions.locations[0];
      handleSelect(l.lat, l.lon);
    }
    if (e.key === 'Escape') setShowDrop(false);
  };

  const hasResults = suggestions.centres.length > 0 || suggestions.locations.length > 0;

  return (
    <div
      ref={containerRef}
      className="absolute top-4 left-1/2 -translate-x-1/2 z-[1002] w-[calc(100%-80px)] max-w-md"
    >
      <div className="flex items-center bg-white rounded-2xl shadow-lg border border-gray-100 overflow-visible">
        <i className="fas fa-search text-[#29752B] text-sm ml-4 flex-shrink-0"></i>
        <input
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setShowDrop(true); }}
          onFocus={() => setShowDrop(true)}
          onKeyDown={handleKey}
          placeholder="Search location or recycling centre…"
          className="flex-1 px-3 py-3 text-sm text-gray-700 bg-transparent focus:outline-none placeholder-gray-400 min-w-0"
        />
        {loading && (
          <div className="w-3.5 h-3.5 border-2 border-[#29752B] border-t-transparent rounded-full animate-spin mr-3 flex-shrink-0" />
        )}
        {query && (
          <button
            onClick={() => { setQuery(''); setSuggestions({ centres: [], locations: [] }); }}
            className="mr-3 text-gray-400 hover:text-gray-600 text-lg leading-none flex-shrink-0"
          >×</button>
        )}
      </div>

      {showDrop && hasResults && (
        <div className="absolute top-full mt-1.5 w-full bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden max-h-80 overflow-y-auto">

          {suggestions.centres.length > 0 && (
            <>
              <div className="px-4 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100">
                <i className="fas fa-recycle mr-1"></i> Recycling Centres
              </div>
              {suggestions.centres.map((point, i) => {
                const [lon, lat] = point.location.coordinates;
                return (
                  <button
                    key={i}
                    onMouseDown={e => { e.preventDefault(); handleSelect(lat, lon, point); }}
                    className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[#EEFAF7] transition border-b border-gray-50"
                  >
                    <div className="w-7 h-7 bg-[#EEFAF7] rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-recycle text-[#29752B] text-xs"></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#172A3A] truncate">{point.name}</p>
                      <p className="text-xs text-gray-400 truncate">{point.address}</p>
                    </div>
                    <i className="fas fa-chevron-right text-gray-300 text-xs ml-auto flex-shrink-0"></i>
                  </button>
                );
              })}
            </>
          )}

          {suggestions.locations.length > 0 && (
            <>
              <div className="px-4 py-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider bg-gray-50 border-b border-gray-100">
                <i className="fas fa-map-marker-alt mr-1"></i> Locations
              </div>
              {suggestions.locations.map((loc, i) => (
                <button
                  key={i}
                  onMouseDown={e => { e.preventDefault(); handleSelect(loc.lat, loc.lon); }}
                  className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[#EEFAF7] transition border-b border-gray-50 last:border-0"
                >
                  <div className="w-7 h-7 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className="fas fa-map-marker-alt text-gray-400 text-xs"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-700 truncate">{loc.short}</p>
                    <p className="text-xs text-gray-400 truncate">{loc.name}</p>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── No-centres nearby request form ─────────────────────── */
const DEVICE_TYPES = ['Laptop', 'Mobile Phone', 'Tablet', 'Desktop PC', 'Monitor', 'TV', 'Printer', 'Battery', 'Cables / Chargers', 'Other'];

function RequestRecyclingPanel() {
  const [formData, setFormData] = useState({ name: '', email: '', address: '', postcode: '', deviceTypes: [], preferredDate: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const toggle = (device) => {
    setFormData(prev => ({
      ...prev,
      deviceTypes: prev.deviceTypes.includes(device)
        ? prev.deviceTypes.filter(d => d !== device)
        : [...prev.deviceTypes, device],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.deviceTypes.length === 0) { setError('Please select at least one device type.'); return; }
    setSubmitting(true); setError('');
    try {
      await API.post('/service-requests', formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl shadow-md p-6 border border-[#A7D3BF] text-center">
        <div className="w-14 h-14 rounded-full bg-[#EEFAF7] flex items-center justify-center mx-auto mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-[#29752B]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-[#172A3A] mb-1">Request received!</h3>
        <p className="text-sm text-[#6E7C87]">We'll bring the nearest recycling to you. Expect a confirmation email soon.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 border border-[#E2E8F0]">
      <div className="flex items-center space-x-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-[#EEFAF7] flex items-center justify-center flex-shrink-0">
          <img src="/images/recycle-sign.png" alt="Recycle" className="w-6 h-6" />
        </div>
        <div>
          <h3 className="text-base font-bold text-[#172A3A]">No centres near you</h3>
          <p className="text-xs text-[#6E7C87]">We're bringing the nearest recycling to you!</p>
        </div>
      </div>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#414042] mb-1">Full Name</label>
            <input type="text" required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Your name" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#414042] mb-1">Email</label>
            <input type="email" required value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="you@example.com" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B]" />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-[#414042] mb-1">Address</label>
            <input type="text" required value={formData.address} onChange={e => setFormData(p => ({ ...p, address: e.target.value }))} placeholder="Street address" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B]" />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#414042] mb-1">Postcode</label>
            <input type="text" value={formData.postcode} onChange={e => setFormData(p => ({ ...p, postcode: e.target.value }))} placeholder="e.g. SE10 9LS" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B]" />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#414042] mb-1">Device types to recycle</label>
          <div className="flex flex-wrap gap-2">
            {DEVICE_TYPES.map(d => (
              <button type="button" key={d} onClick={() => toggle(d)}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${formData.deviceTypes.includes(d) ? 'bg-[#29752B] text-white border-[#29752B]' : 'bg-white text-[#414042] border-gray-300 hover:border-[#29752B]'}`}>
                {d}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-[#414042] mb-1">Preferred collection date</label>
          <input type="date" value={formData.preferredDate} onChange={e => setFormData(p => ({ ...p, preferredDate: e.target.value }))} min={new Date().toISOString().split('T')[0]} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B]" />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#414042] mb-1">Additional notes</label>
          <textarea rows={2} value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} placeholder="Any special instructions…" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#29752B] resize-none" />
        </div>
        {error && <p className="text-xs text-red-500">{error}</p>}
        <button type="submit" disabled={submitting} className="w-full bg-[#29752B] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#18421B] transition-colors disabled:opacity-60">
          {submitting ? 'Submitting…' : 'Request Collection'}
        </button>
      </form>
    </div>
  );
}

/* ─── Main Map Component ─────────────────────────────────── */
function CollectionPointMap({ onMarkerClick, initialSearchQuery }) {
  const [collectionPoints, setCollectionPoints] = useState([]);
  const [bookmarkedLocations, setBookmarkedLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showBookmarkedList, setShowBookmarkedList] = useState(false);
  const [nearbyCount, setNearbyCount] = useState(null);
  const [mapStatus, setMapStatus] = useState('loading');
  const [retryAttempt, setRetryAttempt] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    API.get('/users/profile')
      .then(({ data }) => setBookmarkedLocations(data.bookmarkedLocations || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let retryTimer;
    const fetchCollectionPoints = async (attempt = 1) => {
      if (attempt === 1) setMapStatus('loading');
      else { setMapStatus('retrying'); setRetryAttempt(attempt); }
      try {
        const { data } = await API.get('/collection-points/nearby', {
          params: { longitude: DEFAULT_CENTER[1], latitude: DEFAULT_CENTER[0] },
        });
        setCollectionPoints(data);
        const count = data.filter(point => {
          const [lon, lat] = point.location.coordinates;
          return haversineDistance(DEFAULT_CENTER[0], DEFAULT_CENTER[1], lat, lon) <= CIRCLE_RADIUS;
        }).length;
        setNearbyCount(count);
        setMapStatus('ok');
      } catch (err) {
        if (attempt < 4) {
          const delay = attempt * 8000;
          retryTimer = setTimeout(() => fetchCollectionPoints(attempt + 1), delay);
        } else {
          setNearbyCount(0);
          setMapStatus('failed');
        }
      }
    };
    fetchCollectionPoints();
    return () => clearTimeout(retryTimer);
  }, []);

  /* Pan to URL query on load */
  useEffect(() => {
    if (!initialSearchQuery || collectionPoints.length === 0) return;
    const q = initialSearchQuery.toLowerCase();
    const match = collectionPoints.find(
      p => p.name.toLowerCase().includes(q) || (p.address || '').toLowerCase().includes(q)
    );
    if (match) {
      const [lon, lat] = match.location.coordinates;
      setSelectedLocation([lat, lon]);
      if (onMarkerClick) onMarkerClick(match);
    } else {
      fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(initialSearchQuery)}&format=json&limit=1&countrycodes=gb`,
        { headers: { 'Accept-Language': 'en' } }
      )
        .then(r => r.json())
        .then(data => {
          if (data[0]) setSelectedLocation([parseFloat(data[0].lat), parseFloat(data[0].lon)]);
        })
        .catch(() => {});
    }
  }, [initialSearchQuery, collectionPoints, onMarkerClick]);

  return (
    <div className="h-full">
      <div className="relative h-full">
        {/* Status banners */}
        {mapStatus === 'loading' && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[1001] bg-white/90 backdrop-blur-sm shadow-md rounded-xl px-4 py-2 flex items-center gap-2 text-sm text-[#004d3f] font-medium">
            <svg className="animate-spin w-4 h-4 text-[#34A853]" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
            Loading collection centres…
          </div>
        )}
        {mapStatus === 'retrying' && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[1001] bg-amber-50/95 backdrop-blur-sm shadow-md rounded-xl px-4 py-2 flex items-center gap-2 text-sm text-amber-800 font-medium">
            <svg className="animate-spin w-4 h-4 text-amber-500" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg>
            Server warming up… (attempt {retryAttempt} of 3)
          </div>
        )}
        {mapStatus === 'failed' && (
          <div className="absolute top-16 left-1/2 -translate-x-1/2 z-[1001] bg-red-50/95 backdrop-blur-sm shadow-md rounded-xl px-4 py-2 flex items-center gap-2 text-sm text-red-700 font-medium">
            ⚠ Could not load centres — please refresh the page
          </div>
        )}

        <MapContainer
          center={DEFAULT_CENTER}
          zoom={13}
          className="h-full w-full"
          attributionControl
          zoomControl={false}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <CustomZoomControls />
          <CustomAttribution />
          <Circle
            center={DEFAULT_CENTER}
            radius={CIRCLE_RADIUS}
            pathOptions={{ color: '#004d3f', fillColor: '#004d3f', fillOpacity: 0.07, dashArray: '8 6', weight: 2 }}
          />
          <CircleMarker
            center={DEFAULT_CENTER}
            radius={8}
            pathOptions={{ color: '#004d3f', fillColor: '#004d3f', fillOpacity: 1 }}
          />
          <CompassMarkers />
          {collectionPoints.map((point) => {
            const coordinates = [...point.location.coordinates].reverse();
            return (
              <Marker key={point._id} position={coordinates} icon={customIcon} eventHandlers={{ click: () => onMarkerClick && onMarkerClick(point) }}>
                <Popup>
                  <strong>{point.name}</strong><br />
                  {point.address}<br />
                  {point.hours && <><em>{point.hours}</em><br /></>}
                  <strong>Accepted: </strong>
                  {point.acceptedItems.map((item, index) => (
                    <span key={index}>{item.name}{index < point.acceptedItems.length - 1 && ' | '}</span>
                  ))}
                </Popup>
              </Marker>
            );
          })}
          <PanToLocation location={selectedLocation} />
        </MapContainer>

        {/* Search overlay — visible to ALL users */}
        <MapSearchBox
          collectionPoints={collectionPoints}
          setSelectedLocation={setSelectedLocation}
          onMarkerClick={onMarkerClick}
          initialQuery={initialSearchQuery}
        />

        {/* Bookmarks button — logged-in users only */}
        {localStorage.getItem('token') && (
          <>
            <button
              onClick={() => setShowBookmarkedList(!showBookmarkedList)}
              className="fixed top-[6.4rem] left-4 bg-[#004d3f] text-white p-3 rounded-full shadow-lg z-[9999] hover:bg-green-600"
              data-tooltip-id="savedLocations"
              data-tooltip-content="Saved locations"
              data-tooltip-place="right"
            >
              <i className="fas fa-bookmark text-white"></i>
            </button>
            <Tooltip id="savedLocations" style={{ zIndex: 10000 }} />
          </>
        )}

        <Tooltip anchorSelect=".zoom-in" place="left" style={{ zIndex: 10000 }}>Zoom in</Tooltip>
        <Tooltip anchorSelect=".zoom-out" place="left" style={{ zIndex: 10000 }}>Zoom out</Tooltip>

        {/* Saved centres panel */}
        {showBookmarkedList && (
          <div className="absolute top-[6.4rem] left-16 bg-white rounded-2xl shadow-xl border border-gray-100 w-72 z-[9999] overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h3 className="font-semibold text-sm text-[#172A3A]">♥ Saved Centres</h3>
              <button onClick={() => setShowBookmarkedList(false)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">×</button>
            </div>
            {bookmarkedLocations.length === 0 ? (
              <div className="px-4 py-6 text-center">
                <p className="text-2xl mb-2">🔖</p>
                <p className="text-sm text-gray-500">No saved centres yet.</p>
                <p className="text-xs text-gray-400 mt-1">Tap ♥ on a centre to save it here.</p>
              </div>
            ) : (
              <ul className="max-h-72 overflow-y-auto divide-y divide-gray-50">
                {bookmarkedLocations.map((id, index) => {
                  const point = collectionPoints.find(p => p._id === id || p._id?.toString() === id?.toString());
                  if (!point) return <li key={index} className="px-4 py-3 text-xs text-gray-400 italic">Centre not found</li>;
                  const [lon, lat] = point.location?.coordinates || [0, 0];
                  return (
                    <li key={index} className="px-4 py-3 hover:bg-[#EEFAF7] transition cursor-pointer" onClick={() => { setSelectedLocation([lat, lon]); setShowBookmarkedList(false); }}>
                      <p className="text-sm font-medium text-[#172A3A] truncate">{point.name}</p>
                      <p className="text-xs text-gray-400 truncate mt-0.5">{point.address}</p>
                      <span className="text-xs text-[#29752B] mt-1 inline-block">View on map →</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Radius info badge */}
        <div className="absolute bottom-4 right-4 bg-white bg-opacity-90 rounded-xl px-3 py-2 shadow text-xs text-[#004d3f] font-medium z-[1000]">
          ● Your area (5 km radius)
        </div>
      </div>

      {nearbyCount === 0 && <RequestRecyclingPanel />}
    </div>
  );
}

export default CollectionPointMap;
